import React from "react";
import Image from "next/image";
import { usePropertyContext } from "../../context";
import ExemptionsAnimationWhite from "public/account/exemptions/exemptions-animation-white.gif";
import { sendErrorToWebhook } from "utils/sendWebhook";
import ButtonV2 from "elements/ButtonV2";
import client from "utils/client";
import { useRouter, useSearchParams } from "next/navigation";
import { useExemptionsContext } from "../../ExemptionsContext";
import { property_has_viable_exemption } from "../questionFlowMachines/research.machine";

const NonAgreementConfirmation = ({
	ExitButton,
}: {
	ExitButton?: React.FC;
}) => {
	const { setModalToShow, details, fetchPropertyDetails } =
		usePropertyContext();

	const router = useRouter();
	const searchParams = useSearchParams();
	const { flowId } = useExemptionsContext();

	React.useEffect(() => {
		const submitExemptionComplete = async () => {
			if (!details?.id) return;
			try {
				if (flowId === "RESEARCH_EXEMPTION") {
					await client.submitExemptionsResearchSurveyCompleted({
						propertyId: details?.id,
					});
				} else {
					await client.submitExemptionsDataNotQualified({
						propertyId: details?.id,
					});
				}
			} catch (e) {
				console.error(
					"Error submitting data on the Exemptions Not Qualified Confirmation page " +
						e
				);
				sendErrorToWebhook(
					"** Error submitting data on the Exemptions Not Qualified Confirmation page ** " +
						e
				);
			}
		};

		submitExemptionComplete();
	}, []);

	const onAttemptExit = () => {
		if (!details) return;
		setModalToShow(undefined);
		fetchPropertyDetails(details.id.toString(), {
			autoOpenNARModal: false,
		}).catch(err => {
			console.error(err);
			sendErrorToWebhook(
				"Error caught in onAttemptExit of Exemptions Survey at the Non Agreement Page: " +
					err
			);
		});
	};

	const handleSubmit = async () => {
		router.push(
			`/account/properties/${details?.id}` +
				(searchParams.get("onboarding") ? "?onboarding=true" : "")
		);
		setModalToShow(undefined);
		onAttemptExit();
	};

	return (
		<div className="exemptions-survey-modal-inner-container">
			<div className="exemptions-question-card mt-4">
				<div className="exemptions-intro-animation-box-reversed mt-2 mb-2">
					<div>
						<p className="lg bold">
							That's all for your exemption at the moment!
						</p>
						{flowId === "RESEARCH_EXEMPTION" && (
							<>
								{property_has_viable_exemption(details) && (
									<p className="sm">
										Once we've reviewed the information you've provided, we will
										contact you if we need further information or supporting
										documentation. In the meantime, sit back and let us handle
										the rest!
									</p>
								)}
								{!property_has_viable_exemption(details) && (
									<p className="sm">
										While there is no primary residence exemption available for
										you at this time, we will keep an eye out and alert you if
										it becomes available. If you are a senior, veteran, disabled
										or have other unique circumstances, you may qualify for
										additional exemptions. We recommend contacting the county to
										apply for these, as we are unable to file these exemptions
										on your behalf at this time.
									</p>
								)}
							</>
						)}
						{flowId !== "RESEARCH_EXEMPTION" && (
							<>
								<p className="sm">
									We'll notify you if we uncover other exemptions that could
									apply to you. For now, we'll move on to some other ways to
									save.
								</p>
							</>
						)}
					</div>
					<div className="exemptions-intro-animation">
						<Image
							src={ExemptionsAnimationWhite}
							unoptimized
							alt="Exemptions Table"
							style={{
								maxWidth: "100%",
								height: "auto",
							}}
						/>
					</div>
				</div>
			</div>

			{ExitButton ? (
				<ExitButton />
			) : (
				<ButtonV2
					size="large"
					id="account-property-profile-intro-begin"
					onClick={handleSubmit}>
					Finish
				</ButtonV2>
			)}
		</div>
	);
};

export default NonAgreementConfirmation;
